import { useEffect, useState } from "react";
import { ImagePlus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

interface GuideImage {
  id: string;
  slot: number;
  image_url: string;
  alt_text: string;
}

const AdminGuideImages = () => {
  const [images, setImages] = useState<GuideImage[]>([]);

  const load = async () => {
    const { data } = await (supabase as any)
      .from("guide_images")
      .select("*")
      .order("slot", { ascending: true });
    setImages(data || []);
  };

  useEffect(() => { load(); }, []);

  const upload = async (file: File): Promise<void> => {
    const webp = await compressToWebP(file, { maxSizeMB: 0.4 });
    const path = `guide/img-${Date.now()}.webp`;
    const { error: upErr } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, webp, { contentType: webp.type, upsert: false })
    );
    if (upErr) throw new Error(upErr.message);
    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const maxSlot = images.length ? Math.max(...images.map((i) => i.slot)) : 0;
    const { error } = await (supabase as any).from("guide_images").insert({
      slot: maxSlot + 1,
      image_url: `${urlData.publicUrl}?t=${Date.now()}`,
      alt_text: "Excursion à Essaouira — Maroc Transfers",
    });
    if (error) throw new Error(error.message);
    toast.success("Image ajoutée au slider du guide !");
    load();
  };

  const updateAlt = async (id: string, alt_text: string) => {
    const v = alt_text.trim();
    if (v.length < 10) {
      toast.error("Le texte alternatif est obligatoire (min. 10 caractères, incluez un mot-clé : ex. transfert aéroport Marrakech)");
      return;
    }
    const { error } = await (supabase as any).from("guide_images").update({ alt_text }).eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Alt mis à jour"); load(); }
  };

  const remove = async (id: string) => {
    const { error } = await (supabase as any).from("guide_images").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Image supprimée"); load(); }
  };

  const move = async (img: GuideImage, dir: -1 | 1) => {
    const sorted = [...images];
    const idx = sorted.findIndex((i) => i.id === img.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    await (supabase as any).from("guide_images").update({ slot: swap.slot }).eq("id", img.id);
    await (supabase as any).from("guide_images").update({ slot: img.slot }).eq("id", swap.id);
    load();
  };

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <ImagePlus className="h-5 w-5 text-primary" />
            Guide Essaouira — Slider d'images
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 font-body text-sm text-muted-foreground">
            Ajoutez plusieurs images pour transformer la médias de l'article en carousel.
            Si aucune image n'est ajoutée, une image par défaut est affichée.
          </p>
          <ImageDropZone
            onUpload={upload}
            accept="image/*"
            label="Glissez vos photos d'Essaouira (JPG/PNG/WEBP) — compression WebP automatique"
            height="h-28"
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {images.map((img, i) => (
          <Card key={img.id} className="border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="font-heading text-base">Slide {img.slot}</CardTitle>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(img, -1)}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" disabled={i === images.length - 1} onClick={() => move(img, 1)}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(img.id)} className="text-destructive hover:text-destructive/80">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <img src={img.image_url} alt={img.alt_text} className="h-40 w-full rounded-md bg-secondary object-cover" loading="lazy" />
              <div>
                <label className="mb-1 block font-body text-xs font-semibold text-foreground">
                  Texte alternatif (Alt) <span className="text-destructive">*</span>
                </label>
                <Input
                  required
                  defaultValue={img.alt_text}
                  placeholder="ex : Vue de la baie d'Essaouira au coucher du soleil"
                  onBlur={(e) => { if (e.target.value !== img.alt_text) updateAlt(img.id, e.target.value); }}
                />
                <p className="mt-1 font-body text-[11px] text-muted-foreground">
                  Décrivez l'image pour un malvoyant en incluant un mot-clé (ex : transfert aéroport Marrakech).
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminGuideImages;